import { useCallback, useEffect, useState } from "react";
import type { AnswerState, Exercise } from "../types/exercises";
import { solutionFor } from "../utils/practice";

export type LessonPhase = "answering" | "checked";

const emptyAnswer: AnswerState = { ready: false, correct: false };

export function useLessonRunner(exercises: Exercise[], unlimitedHearts: boolean, onChecked?: (ex: Exercise, correct: boolean) => void) {
  const [index, setIndex] = useState(0);
  const [answer, setAnswer] = useState<AnswerState>(emptyAnswer);
  const [phase, setPhase] = useState<LessonPhase>("answering");
  const [hearts, setHearts] = useState(5);
  const [correctCount, setCorrectCount] = useState(0);
  const [mistakes, setMistakes] = useState(0);
  const [finished, setFinished] = useState(false);
  const [startedAt] = useState(() => Date.now());
  const [skipped, setSkipped] = useState(false);

  const exercise = exercises[index];

  const check = useCallback(
    (a: AnswerState = answer) => {
      if (!a.ready || phase === "checked") return;
      setPhase("checked");
      if (a.correct) {
        setCorrectCount((c) => c + 1);
      } else {
        setMistakes((m) => m + 1);
        if (!unlimitedHearts) setHearts((h) => Math.max(0, h - 1));
      }
      onChecked?.(exercise, a.correct);
    },
    [answer, phase, unlimitedHearts, onChecked, exercise]
  );

  useEffect(() => {
    if (answer.auto && phase === "answering") check(answer);
  }, [answer, phase, check]);

  const skip = useCallback(() => {
    if (phase === "checked") return;
    setSkipped(true);
    setAnswer({ ready: true, correct: false, solution: solutionFor(exercise) });
    setPhase("checked");
    setMistakes((m) => m + 1);
  }, [phase, exercise]);

  const next = useCallback(() => {
    if (index + 1 >= exercises.length) {
      setFinished(true);
      return;
    }
    setIndex((i) => i + 1);
    setAnswer(emptyAnswer);
    setPhase("answering");
    setSkipped(false);
  }, [index, exercises.length]);

  const refillHearts = useCallback(() => setHearts(5), []);

  const xp = correctCount * 10 + (mistakes === 0 ? 5 : 0);
  const accuracy = exercises.length ? Math.round((correctCount / exercises.length) * 100) : 0;

  return {
    index,
    exercise,
    answer,
    setAnswer,
    phase,
    hearts,
    check,
    skip,
    next,
    skipped,
    finished,
    correctCount,
    mistakes,
    xp,
    accuracy,
    startedAt,
    refillHearts,
    progress: ((index + (phase === "checked" ? 1 : 0)) / exercises.length) * 100,
  };
}
